import React, { useState } from "react";
import {
  Box,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { FaFacebook, FaLinkedin } from "react-icons/fa";
import { FaSquareInstagram, FaLocationDot } from "react-icons/fa6";
import { MdAddCall, MdEmail } from "react-icons/md";
import logo from "@assets/images/DESCO-REALTY-LOGO-White.png";
import Popup from "../../pages/Home/Popup";
import {ROUTES} from "../../../constants/routes"
// Styled Components
const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "black", // Dark blue background
  color: "#fff",
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(4),
}));

const FooterLogo = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const FooterHeading = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  position: "relative",
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(1.5),
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "40px",
    height: "3px",
    backgroundColor: "#C10E14", // Red accent line
  },
}));

const FooterLink = styled(Typography)(({ theme }) => ({
  color: "#ddd",
  textDecoration: "none",
  transition: "all 0.3s ease",
  marginBottom: theme.spacing(1),
  display: "block",
  "&:hover": {
    color: "#fff",
    paddingLeft: "5px",
  },
}));

const SocialIconsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SocialIcon = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(255,255,255,0.1)",
  borderRadius: "50%",
  width: "40px",
  height: "40px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#C10E14",
    transform: "translateY(-3px)",
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
  "& svg": {
    color: "#C10E14",
    marginRight: theme.spacing(1.5),
    marginTop: "4px",
  },
}));

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <FooterContainer sx={{ px: { xs: 2, sm: 0 } }}>
      <div>
        <Popup open={open} handleClose={handleClose} />
      </div>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo and About Section */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FooterLogo>
              {/* Replace with your actual logo */}
              <Link to="/">
                <Box
                  component="img"
                  src={logo}
                  alt="Desco Real Estate"
                  sx={{
                    height: "60px",
                    width: "auto",
                    mb: 2,
                  }}
                />
              </Link>
            </FooterLogo>
            <Typography variant="body2" sx={{ mb: 3, color: "#aab" }}>
              Desco Real Estate provides premium real estate services in Dubai,
              helping clients find their perfect property with expert guidance
              and personalized support.
            </Typography>
            <SocialIconsContainer>
              <SocialIcon>
                <a
                  href="https://www.facebook.com/descorealestate/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaFacebook size={20} color="white" />
                </a>
              </SocialIcon>
              <SocialIcon>
                <a
                  href="https://www.instagram.com/descorealestate/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaSquareInstagram size={20} color="white" />
                </a>
              </SocialIcon>
              <SocialIcon>
                <a
                  href="https://www.linkedin.com/company/desco-real-estatedxb/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaLinkedin size={20} color="white" />
                </a>
              </SocialIcon>
              {/* <SocialIcon>
                <a href="https://www.youtube.com/@descorealestate" target="_blank" rel="noopener noreferrer">
                <FaYoutube size={20} />
                </a>
              </SocialIcon> */}
            </SocialIconsContainer>
          </Grid>

          {/* Quick Links 1 */}
          <Grid item xs={6} sm={6} md={2} lg={2}>
            <FooterHeading variant="h6">Properties</FooterHeading>
            <Box>
              <FooterLink component={Link} to={ROUTES.BUY}>
                Buy
              </FooterLink>
              <FooterLink component={Link} to={ROUTES.RENT}>
                Rent
              </FooterLink>
              <FooterLink component={Link} to={ROUTES.OFF_PLAN}>
                Off Plan
              </FooterLink>
              <FooterLink component={Link} to={ROUTES.AREAS}>
                Areas
              </FooterLink>
              <FooterLink component={Link} onClick={handleOpen}>
                Market Report
              </FooterLink>
            </Box>
          </Grid>

          {/* Quick Links 2 */}
          <Grid item xs={6} sm={6} md={2} lg={2}>
            <FooterHeading variant="h6">Company</FooterHeading>
            <Box>
              <FooterLink component="a" href="/blog">
                Blogs
              </FooterLink>
              <FooterLink component={Link} to={ROUTES.SERVICES}>
                Our Services
              </FooterLink>
              <FooterLink component={Link} to={ROUTES.ABOUT_US}>
                About Us
              </FooterLink>
              <FooterLink component={Link} to={ROUTES.CONTACT_US}>
                Contact Us
              </FooterLink>
              <FooterLink component={Link} to={ROUTES.LIST_PROPERTY}>
                List With Us
              </FooterLink>
            </Box>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} sm={6} md={4} lg={5}>
            <FooterHeading variant="h6">Contact Us</FooterHeading>
            <Stack spacing={2}>
              <ContactItem>
                <MdAddCall size={20} />
                <Typography variant="body2">+971 4 576 0110</Typography>
              </ContactItem>
              <ContactItem>
                <MdEmail size={20} />
                <Typography variant="body2">
                  <a
                    href="https://mail.google.com/mail/?view=cm&to=info@descorealestate.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    info@descorealestate.com
                  </a>
                </Typography>
              </ContactItem>

              <ContactItem>
                <FaLocationDot size={20} />

                <a
                  href="https://www.google.com/maps/dir//Irise+Tower+-+Barsha+Heights+-+Dubai+-+United+Arab+Emirates/@25.0973376,55.0984708,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x3e5f6b3f78b28531:0x2d4e9be5f4fce9fa!2m2!1d55.1808723!2d25.0973604?hl=en-GB&authuser=0&entry=ttu&g_ep=EgoyMDI1MDYwMi4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Typography variant="body2">
                   Office 10C-08, I-Rise Tower, Barsha Heights, Dubai, UAE
                  </Typography>
                </a>
              </ContactItem>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 4, mb: 3, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#aab", mb: isMobile ? 1 : 0 }}
          >
            © {new Date().getFullYear()} Copyright@Desco Real Estate
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              color: "#aab",
              fontSize: "14px",
            }}
          >
            <Typography
              component="a"
              variant="body2"
              sx={{
                color: "inherit",
                textDecoration: "none",
                "&:hover": { color: "#fff" },
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              component="a"
              variant="body2"
              sx={{
                color: "inherit",
                textDecoration: "none",
                "&:hover": { color: "#fff" },
              }}
            >
              Terms of Service
            </Typography>
          </Box>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
